import { useMemo, useRef } from "react";

export type SubmitOnceGuard = {
  /**
   * Checks and engages the latch in one synchronous call. `true` means "go
   * ahead, you own the submit"; `false` means a submit is already in flight
   * (or finished and was deliberately left latched), so the caller returns
   * without calling the mutation.
   */
  begin: () => boolean;
  /** Re-arms the guard. Only an explicit call does this: nothing else will. */
  release: () => void;
};

/**
 * Synchronous submit-once guard for handlers that move money, create a record
 * or verify an OTP.
 *
 * A disabled button is not enough on its own: `disabled` only lands once React
 * re-renders after the first click, and RTK Query's `isLoading` only flips once
 * the mutation has been dispatched. A second click (or an Enter key plus an
 * auto-submit) in the same tick sees the button still enabled and fires the
 * mutation again. A ref is read and written in the same call, so the second
 * caller is refused before it reaches the network.
 *
 * Release policy is the caller's, per `.claude/rules/web/domains/submit-once.md`
 * → "Release cases": stay latched after a success that navigates away or
 * unmounts, and re-arm on a deliberate host signal after a success that stays
 * mounted. On a failure, follow case 3 there and classify it first with
 * `classifyMutationFailure` (`utils/mutationOutcome.ts`): a definite failure
 * (any 4xx, or a status-less local error) releases so the user can retry; an
 * unknown failure (network error, timeout, 5xx) may have landed, so re-read
 * the resource first and stay latched when the read cannot tell. Never retry
 * the mutation itself, and never release in a `finally`.
 *
 * The latch lives in a ref, so it survives re-renders and dies with the
 * mount: a fresh mount always starts armed.
 */
export function useSubmitOnce(): SubmitOnceGuard {
  const inFlightRef = useRef(false);

  return useMemo<SubmitOnceGuard>(
    () => ({
      begin: () => {
        if (inFlightRef.current) return false;
        inFlightRef.current = true;
        return true;
      },
      release: () => {
        inFlightRef.current = false;
      },
    }),
    []
  );
}
